'use client';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useFloating, autoUpdate, offset, flip, shift, useClick, useDismiss, useInteractions, Placement } from '@floating-ui/react';

interface DropdownProps {
    button: React.ReactNode;
    children: React.ReactNode | ((utils: { close: () => void }) => React.ReactNode);
    btnClassName?: string;
    placement?: Placement;
    offset?: number | number[] | { mainAxis?: number; crossAxis?: number };
}

const Dropdown = forwardRef<{ close: () => void }, DropdownProps>((props, forwardedRef) => {
    const [isOpen, setIsOpen] = useState(false);

    const getOffset = () => {
        if (!props.offset) return 0;
        if (typeof props.offset === 'number') return props.offset;
        if (Array.isArray(props.offset)) {
            return {
                mainAxis: props.offset[0],
                crossAxis: props.offset[1] ?? 0,
            };
        }
        return props.offset;
    };

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: props.placement || 'bottom-end',
        whileElementsMounted: autoUpdate,
        middleware: [offset(getOffset()), flip(), shift()],
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);

    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

    useImperativeHandle(forwardedRef, () => ({
        close() {
            setIsOpen(false);
        },
    }));

    const close = () => setIsOpen(false);

    return (
        <>
            <button ref={refs.setReference} type="button" className={props.btnClassName} {...getReferenceProps()}>
                {props.button}
            </button>

            {isOpen && (
                <div ref={refs.setFloating} style={floatingStyles} className="z-50" {...getFloatingProps()}>
                    {typeof props.children === 'function' ? props.children({ close }) : props.children}
                </div>
            )}
        </>
    );
});

Dropdown.displayName = 'Dropdown';

export default Dropdown;
